import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../axios/axiosInstance';

const fetchInvites = async () => (await axiosInstance.get('/invites/list')).data;

const InviteManagement = () => {
    const queryClient = useQueryClient();
    const [form, setForm] = useState({ email: '', inviteType: 'accountant', firmId: '' });
    const [showForm, setShowForm] = useState(false);
    const [toast, setToast] = useState(null);
    const [filter, setFilter] = useState('all');

    const { data: invites = [], isLoading } = useQuery({ queryKey: ['invites'], queryFn: fetchInvites });

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const sendMutation = useMutation({
        mutationFn: (data) => axiosInstance.post('/invites/send', data),
        onSuccess: (res) => {
            showToast(`Invite sent to ${form.email}`);
            setForm({ email: '', inviteType: 'accountant', firmId: '' });
            setShowForm(false);
            queryClient.invalidateQueries(['invites']);
        },
        onError: (err) => showToast(err.response?.data?.message || 'Failed to send invite', 'error'),
    });

    const revokeMutation = useMutation({
        mutationFn: (id) => axiosInstance.delete(`/invites/${id}`),
        onSuccess: () => {
            showToast('Invite revoked');
            queryClient.invalidateQueries(['invites']);
        },
        onError: () => showToast('Failed to revoke invite', 'error'),
    });

    const handleSend = (e) => {
        e.preventDefault();
        if (!form.email) return showToast('Email is required', 'error');
        sendMutation.mutate({ email: form.email, inviteType: form.inviteType, firmId: form.firmId || null });
    };

    const filtered = filter === 'all' ? invites : invites.filter(i => i.status === filter);

    const statusBadge = (status) => {
        const colors = { pending: '#f59e0b', used: '#10b981', revoked: '#ef4444', expired: '#6b7280' };
        return (
            <span style={{ background: colors[status] || '#6b7280', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 700, padding: '2px 10px' }}>
                {status}
            </span>
        );
    };

    const isExpired = (invite) => invite.status === 'pending' && new Date(invite.expiresAt) < new Date();

    return (
        <div style={{ padding: '24px 32px' }}>
            {toast && (
                <div style={{ position: 'fixed', top: 24, right: 24, background: toast.type === 'error' ? '#ef4444' : '#10b981', color: '#fff', padding: '12px 20px', borderRadius: 8, zIndex: 9999, fontWeight: 600 }}>
                    {toast.msg}
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>Invite Management</h1>
                    <p style={{ color: '#6b7280', marginTop: 4 }}>Invite accountants and companies to join PayrollNoot.</p>
                </div>
                <button onClick={() => setShowForm(!showForm)} style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>
                    + Send Invite
                </button>
            </div>

            {showForm && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 24 }}>
                    <h3 style={{ margin: '0 0 16px', fontWeight: 700 }}>New Invite</h3>
                    <form onSubmit={handleSend} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: '1 1 250px' }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Email Address *</label>
                            <input
                                type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="accountant@firm.com"
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
                                required
                            />
                        </div>
                        <div style={{ flex: '1 1 180px' }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Invite Type</label>
                            <select value={form.inviteType} onChange={e => setForm({ ...form, inviteType: e.target.value })}
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                                <option value="accountant">Accountant</option>
                                <option value="company">Company (Employer)</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button type="submit" disabled={sendMutation.isPending}
                                style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 600, cursor: 'pointer' }}>
                                {sendMutation.isPending ? 'Sending...' : 'Send Invite'}
                            </button>
                            <button type="button" onClick={() => setShowForm(false)}
                                style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer' }}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['all', 'pending', 'used', 'revoked'].map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                        style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #e5e7eb', background: filter === f ? '#1B75F0' : '#fff', color: filter === f ? '#fff' : '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                ))}
            </div>

            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            {['Email', 'Type', 'Status', 'Sent', 'Expires', 'Actions'].map(h => (
                                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>No invites found.</td></tr>
                        ) : filtered.map(invite => (
                            <tr key={invite.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px 16px', fontWeight: 500 }}>{invite.email}</td>
                                <td style={{ padding: '12px 16px' }}>
                                    <span style={{ background: invite.inviteType === 'accountant' ? '#dbeafe' : '#dcfce7', color: invite.inviteType === 'accountant' ? '#1d4ed8' : '#15803d', borderRadius: 20, fontSize: 12, fontWeight: 600, padding: '2px 10px' }}>
                                        {invite.inviteType === 'accountant' ? 'Accountant' : 'Company'}
                                    </span>
                                </td>
                                <td style={{ padding: '12px 16px' }}>{isExpired(invite) ? statusBadge('expired') : statusBadge(invite.status)}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{new Date(invite.createdAt).toLocaleDateString()}</td>
                                <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{new Date(invite.expiresAt).toLocaleDateString()}</td>
                                <td style={{ padding: '12px 16px' }}>
                                    {invite.status === 'pending' && !isExpired(invite) && (
                                        <button onClick={() => revokeMutation.mutate(invite.id)}
                                            style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                            Revoke
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default InviteManagement;
